<script setup lang="ts">
/**
 * Connections, and the sites each has been granted (ADR 0057 §1).
 *
 * "Pick from what our Azure admin has already allowed" is the right flow and
 * Microsoft provides no API for it: an app with Sites.Selected cannot list
 * the sites it was granted. So this list is ours, and every entry is proven
 * by a probe rather than discovered — the chip is the probe's verdict, never
 * anybody's claim.
 *
 * Collapsed at rest, to one line. This is set up a few times a year; it
 * should not sit open above the thing people come here weekly to do. It
 * opens itself only when a site is waiting on someone — that is the one
 * state worth interrupting for.
 *
 * Adding a site here grants nothing. It records the wish and produces the
 * exact command for the tenant admin, app id and site filled in; the entry
 * turns green on its own once the grant lands. That replaces a letter to
 * Group IT and a guess about whether it worked.
 */
import { vWrite } from "../admin-read-only";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import {
  AdminError,
  type ConnectionRow,
  type GrantedResource,
  addGrantedResource,
  deleteGrantedResource,
  getConnections,
  recheckGrantedResource,
} from "../api/admin";
import { useConfirm } from "../composables/useConfirm";
import { useToast } from "../composables/useToast";
import { fmtAge, fmtDateTime } from "../utils/datetime";
import StatusChip from "./StatusChip.vue";

const props = defineProps<{ devUser: string }>();
const emit = defineEmits<{ (e: "changed"): void }>();
const toast = useToast();
const { confirm } = useConfirm();

const connections = ref<ConnectionRow[]>([]);
const probing = ref(true);
const loaded = ref(false);
const open = ref(false);
const detailOpen = ref<Set<number>>(new Set());
const newUrl = ref<Record<string, string>>({});
const addError = ref<Record<string, string>>({});
const busy = ref(false);

let timer: ReturnType<typeof setTimeout> | null = null;
let openedOnce = false;

const all = computed(() => connections.value.flatMap((c) => c.resources));
const waiting = computed(
  () => all.value.filter((r) => r.status !== "granted").length,
);
const checking = computed(() =>
  all.value.some((r) => r.status === "unverified"),
);

const summary = computed(() => {
  const n = connections.value.length;
  const granted = all.value.length - waiting.value;
  const parts = [
    `${n} ${n === 1 ? "connection" : "connections"}`,
    `${granted} ${granted === 1 ? "site" : "sites"} granted`,
  ];
  if (waiting.value) parts.push(`${waiting.value} waiting`);
  return parts.join(" · ");
});

async function reload(): Promise<void> {
  try {
    const res = await getConnections(props.devUser);
    connections.value = res.connections;
    probing.value = res.probing;
  } catch (e) {
    if (!(e instanceof AdminError)) throw e;
  } finally {
    loaded.value = true;
  }
  if (!openedOnce && waiting.value) open.value = true;
  openedOnce = true;
  if (timer) clearTimeout(timer);
  // The probe runs on the sources Job's 10-minute tick; ask gently.
  if (checking.value && probing.value) timer = setTimeout(reload, 30000);
}

onMounted(reload);
onBeforeUnmount(() => {
  if (timer) clearTimeout(timer);
});

type Chip = { tone: "ok" | "warn" | "bad" | "info"; text: string };

function chip(r: GrantedResource): Chip {
  switch (r.status) {
    case "granted":
      return { tone: "ok", text: "granted" };
    case "not_granted":
      return { tone: "warn", text: "waiting for grant" };
    case "not_found":
      return { tone: "bad", text: "no such site" };
    case "error":
      return { tone: "bad", text: "could not check" };
    default:
      return {
        tone: "info",
        text: probing.value ? "checking · within 10 min" : "not checked",
      };
  }
}

function shortAddress(r: GrantedResource): string {
  return r.address.replace(/^https:\/\//, "");
}

function toggleDetail(id: number): void {
  if (detailOpen.value.has(id)) detailOpen.value.delete(id);
  else detailOpen.value.add(id);
  detailOpen.value = new Set(detailOpen.value);
}

async function add(c: ConnectionRow): Promise<void> {
  const url = (newUrl.value[c.key] ?? "").trim();
  if (!url || busy.value) return;
  busy.value = true;
  addError.value[c.key] = "";
  try {
    const row = await addGrantedResource(c.key, url, props.devUser);
    newUrl.value[c.key] = "";
    // Open the new entry: the grant command is the next thing they need.
    detailOpen.value = new Set([...detailOpen.value, row.id]);
    await reload();
    emit("changed");
  } catch (e) {
    addError.value[c.key] = e instanceof Error ? e.message : String(e);
  } finally {
    busy.value = false;
  }
}

async function recheck(r: GrantedResource): Promise<void> {
  try {
    await recheckGrantedResource(r.id, props.devUser);
    await reload();
  } catch (e) {
    toast.error(
      "Could not re-check",
      e instanceof Error ? e.message : String(e),
    );
  }
}

async function remove(r: GrantedResource): Promise<void> {
  const ok = await confirm({
    title: "Take this site off the list?",
    message:
      `${r.title || shortAddress(r)} will no longer be offered when adding a ` +
      "source. This does not revoke the app's access to the site — only the " +
      "tenant admin can do that, in Entra.",
    confirmLabel: "Remove from list",
    danger: true,
  });
  if (!ok) return;
  try {
    await deleteGrantedResource(r.id, props.devUser);
    await reload();
    emit("changed");
  } catch (e) {
    toast.error("Could not remove", e instanceof Error ? e.message : String(e));
  }
}

async function copy(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch {
    toast.error("Could not copy", "Select the text and copy it by hand.");
  }
}
</script>

<template>
  <section v-if="loaded && connections.length" class="conns">
    <button
      class="conns__bar"
      :aria-expanded="open"
      aria-controls="conns-body"
      @click="open = !open"
    >
      <span class="conns__title">Connections</span>
      <span class="conns__summary">{{ summary }}</span>
      <span class="conns__chev" aria-hidden="true">{{ open ? "▾" : "▸" }}</span>
    </button>

    <div v-if="open" id="conns-body" class="conns__body">
      <p v-if="!probing" class="notice">
        Nothing checks site access on this deployment — the sources job is not
        deployed here, so entries stay “not checked”.
      </p>

      <div v-for="c in connections" :key="c.key" class="conn">
        <div class="conn__head">
          <span class="conn__label">{{ c.label }}</span>
          <span class="conn__meta">
            {{ c.permission_model
            }}<template v-if="c.granted_by">
              · access granted by {{ c.granted_by }}</template
            >
          </span>
        </div>

        <ul class="sites">
          <li v-for="r in c.resources" :key="r.id" class="site">
            <div class="site__row">
              <span class="site__name">{{ r.title || shortAddress(r) }}</span>
              <StatusChip
                :tone="chip(r).tone"
                dot
                :title="
                  r.last_probed_at
                    ? `Checked ${fmtDateTime(r.last_probed_at)}`
                    : 'Not checked yet'
                "
              >
                {{ chip(r).text }}
              </StatusChip>
              <span v-if="r.status === 'granted'" class="site__meta">
                {{ r.drives.length }}
                {{ r.drives.length === 1 ? "library" : "libraries" }}
                <template v-if="r.used_by">
                  · {{ r.used_by }}
                  {{ r.used_by === 1 ? "source" : "sources" }}</template
                >
                · checked {{ fmtAge(r.last_probed_at) }}
              </span>
              <span class="spacer" />
              <button
                class="link"
                :aria-expanded="detailOpen.has(r.id)"
                @click="toggleDetail(r.id)"
              >
                {{
                  detailOpen.has(r.id)
                    ? "hide"
                    : r.status === "granted"
                      ? "details"
                      : "how to get access"
                }}
              </button>
            </div>

            <div v-if="detailOpen.has(r.id)" class="site__detail">
              <p v-if="r.title" class="site__addr">{{ shortAddress(r) }}</p>
              <p v-if="r.note" class="site__how">{{ r.note }}</p>

              <ul v-if="r.checks.length" class="checks">
                <li v-for="k in r.checks" :key="k.name" class="check">
                  <span
                    class="check__mark"
                    :class="{
                      'check__mark--ok': k.ok === true,
                      'check__mark--bad': k.ok === false,
                    }"
                    aria-hidden="true"
                    >{{
                      k.ok === true ? "✓" : k.ok === false ? "✕" : "–"
                    }}</span
                  >
                  <span class="check__name">{{ k.name }}</span>
                  <span class="check__msg">{{ k.message }}</span>
                </li>
              </ul>

              <template v-if="r.grant">
                <p class="site__how">
                  Send this to {{ c.granted_by || "your tenant admin" }}. It
                  gives <strong>{{ c.app_name }}</strong> read access to this
                  one site and nothing else. This entry turns green on its own
                  once it has been run.
                </p>
                <div class="cmd">
                  <pre>{{ r.grant.pnp }}</pre>
                  <button class="link" @click="copy(r.grant.pnp)">copy</button>
                </div>
                <details class="alt">
                  <summary>Without PnP PowerShell (Microsoft Graph)</summary>
                  <div class="cmd">
                    <pre>{{ r.grant.graph }}</pre>
                    <button class="link" @click="copy(r.grant.graph)">
                      copy
                    </button>
                  </div>
                </details>
              </template>

              <div class="site__actions">
                <button v-write class="link" @click="recheck(r)">check again</button>
                <button
                  v-if="!r.declared"
                  class="link link--danger"
                  v-write
                  @click="remove(r)"
                >
                  remove from list
                </button>
              </div>
            </div>
          </li>
          <li v-if="!c.resources.length" class="site site--empty">
            No sites yet. Paste a site's address below to get the command that
            grants access to it.
          </li>
        </ul>

        <form v-write class="add" @submit.prevent="add(c)">
          <input
            v-model="newUrl[c.key]"
            type="url"
            :aria-label="`SharePoint site address to add to ${c.label}`"
            placeholder="https://company.sharepoint.com/sites/…"
          />
          <button
            class="btn"
            type="submit"
            :disabled="busy || !(newUrl[c.key] ?? '').trim()"
          >
            Add site
          </button>
        </form>
        <p v-if="addError[c.key]" class="error" role="alert">
          {{ addError[c.key] }}
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.conns {
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}
.conns__bar {
  display: flex;
  align-items: baseline;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  font: inherit;
  color: inherit;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
}
.conns__title {
  font-weight: 650;
}
.conns__summary {
  flex: 1;
  font-size: 0.85rem;
  color: var(--color-muted);
}
.conns__chev {
  color: var(--color-muted);
}
.conns__body {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: 0 0.75rem 0.75rem;
}
.conn__head {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  flex-wrap: wrap;
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-border);
}
.conn__label {
  font-weight: 600;
}
.conn__meta {
  font-size: 0.8rem;
  color: var(--color-muted);
}
.sites {
  list-style: none;
  margin: 0.35rem 0;
  padding: 0;
}
.site {
  padding: 0.35rem 0;
}
.site--empty {
  font-size: 0.85rem;
  color: var(--color-muted);
}
.site__row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
}
.site__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.site__meta {
  font-size: 0.8rem;
  color: var(--color-muted);
  white-space: nowrap;
}
.spacer {
  flex: 1;
}
.site__detail {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 0.4rem 0 0.2rem;
  padding: 0.6rem 0.75rem;
  background: var(--color-surface-alt);
  border-radius: var(--radius-sm);
}
.site__addr,
.site__how {
  margin: 0;
  font-size: 0.85rem;
}
.site__addr {
  font-family: var(--font-mono, monospace);
  color: var(--color-muted);
}
.checks {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.check {
  display: grid;
  grid-template-columns: 1.1rem 8rem 1fr;
  gap: 0.4rem;
  font-size: 0.85rem;
}
.check__mark {
  color: var(--color-muted);
  font-weight: 700;
}
.check__mark--ok {
  color: var(--color-success);
}
.check__mark--bad {
  color: var(--color-error);
}
.check__name {
  color: var(--color-muted);
}
.cmd {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
}
.cmd pre {
  flex: 1;
  margin: 0;
  padding: 0.5rem 0.6rem;
  font-family: var(--font-mono, monospace);
  font-size: 0.78rem;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
}
.alt summary {
  font-size: 0.82rem;
  color: var(--color-muted);
  cursor: pointer;
}
.alt .cmd {
  margin-top: 0.4rem;
}
.site__actions {
  display: flex;
  gap: 0.9rem;
}
.add {
  display: flex;
  gap: 0.5rem;
}
.add input {
  flex: 1;
  font: inherit;
  font-size: 0.9rem;
  color: var(--color-ink);
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  padding: 0.4rem 0.6rem;
}
.add input:focus {
  outline: none;
  border-color: var(--color-accent);
}
.notice,
.error {
  margin: 0;
  padding: 0.5rem 0.7rem;
  font-size: 0.85rem;
  border-radius: var(--radius-sm);
}
.notice {
  color: var(--color-warn-ink);
  background: var(--color-warn-bg);
}
.error {
  margin-top: 0.4rem;
  color: var(--color-error);
  background: var(--color-error-bg);
}
button.link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.85rem;
  color: var(--color-accent);
  cursor: pointer;
  white-space: nowrap;
}
button.link.link--danger {
  color: var(--color-error);
}
</style>
